import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Check, Hash, Loader2, MessageCircle, Send, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { channelDisplayName } from '@/lib/chat-display';
import { ARCHE_PATH } from '../constants';

/** Longest optional note an admin can attach to the shared link. */
const NOTE_MAX_LENGTH = 500;

/** Debounce before firing the people search, matching the chat composer's. */
const USER_SEARCH_DEBOUNCE_MS = 250;

interface ShareableTicket {
  ticketId: string;
  ticketNumber: number;
  subject: string;
}

interface PersonResult {
  userId: string;
  username: string;
  displayName: string;
}

/** Where the link is headed: an existing channel, or a DM to be opened on send. */
type Target =
  | { kind: 'channel'; channelId: string; label: string }
  | { kind: 'user'; label: string; userId: string };

interface SendTicketToChatDialogProps {
  ticket: ShareableTicket | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Absolute so the link survives being forwarded out of the app. */
export function ticketAdminUrl(ticketId: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${ARCHE_PATH}/admin/tickets/${ticketId}`;
}

/**
 * The chat message body. Chat stores messages as HTML and renders them through
 * `sanitizeChatHtml`, which permits `<p>`, `<strong>` and `<a href>` — so the
 * link arrives clickable. Every interpolated value is escaped here because the
 * subject and note are attacker-controllable free text (the customer writes the
 * subject), and the sanitizer is a backstop, not a licence to inject.
 */
export function buildTicketShareHtml(ticket: ShareableTicket, note: string): string {
  const url = escapeHtml(ticketAdminUrl(ticket.ticketId));
  const parts = [
    `<p>Support ticket <strong>#${ticket.ticketNumber}</strong> — <a href="${url}">${escapeHtml(ticket.subject)}</a></p>`,
  ];
  const trimmedNote = note.trim();
  if (trimmedNote) {
    // Keep the admin's line breaks — each line becomes its own paragraph.
    for (const line of trimmedNote.split(/\n+/)) {
      parts.push(`<p>${escapeHtml(line)}</p>`);
    }
  }
  return parts.join('');
}

export function SendTicketToChatDialog({
  ticket,
  open,
  onOpenChange,
}: SendTicketToChatDialogProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [target, setTarget] = useState<Target | null>(null);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fresh dialog every time it opens — a stale target from the previous ticket
  // is the one mistake that can't be undone once the message is posted.
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setDebouncedSearch('');
    setTarget(null);
    setNote('');
    setSentTo(null);
    setError(null);
  }, [open]);

  // Debounce the people-search term, matching the chat composer's cadence.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search.trim()), USER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search]);

  const channelsQuery = useQuery({
    queryKey: queryKeys.customerSupport.shareTargetChannels(),
    queryFn: async () => (await api.listChatChannels({ limit: 100 })).channels,
    enabled: open,
  });
  const channels = useMemo(() => channelsQuery.data ?? [], [channelsQuery.data]);
  const loadingChannels = channelsQuery.isLoading;

  // People search covers anyone you have no DM with yet. A failed search reads
  // as no extra people (the channel list is unaffected).
  const peopleQuery = useQuery({
    queryKey: queryKeys.customerSupport.shareTargetPeople(debouncedSearch),
    queryFn: async () => (await api.searchUsersForDm(debouncedSearch)).users as PersonResult[],
    enabled: open && debouncedSearch.length >= 2,
  });
  const people = useMemo(
    () => (debouncedSearch.length >= 2 ? (peopleQuery.data ?? []) : []),
    [peopleQuery.data, debouncedSearch],
  );

  const filteredChannels = useMemo(() => {
    const query = search.trim().toLowerCase();
    const named = channels.map((channel) => ({ channel, label: channelDisplayName(channel) }));
    if (!query) return named;
    return named.filter((entry) => entry.label.toLowerCase().includes(query));
  }, [channels, search]);

  // Someone already in the channel list shouldn't appear twice.
  const dmPartnerIds = useMemo(
    () => new Set(channels.map((c) => c.dmPartnerId).filter((id): id is string => !!id)),
    [channels],
  );
  const filteredPeople = useMemo(
    () => people.filter((person) => !dmPartnerIds.has(person.userId)),
    [people, dmPartnerIds],
  );

  const handleSend = useCallback(async () => {
    if (!ticket || !target || sending) return;
    setSending(true);
    setError(null);
    try {
      // A DM target may not have a channel yet; opening one is idempotent.
      const channelId =
        target.kind === 'channel'
          ? target.channelId
          : (await api.getOrCreateDirectChannel(target.userId, { isUserId: true })).channel
              .channelId;
      await api.sendChatMessage(channelId, { content: buildTicketShareHtml(ticket, note) });
      setSentTo(target.label);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send the ticket.');
    } finally {
      setSending(false);
    }
  }, [ticket, target, note, sending]);

  const nothingToShow =
    !loadingChannels && filteredChannels.length === 0 && filteredPeople.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Send ticket to chat</DialogTitle>
          <DialogDescription>
            {ticket
              ? `Posts a link to ticket #${ticket.ticketNumber} in the conversation you pick.`
              : 'Posts a link to this ticket in the conversation you pick.'}
          </DialogDescription>
        </DialogHeader>

        {sentTo ? (
          <div className="flex items-center gap-2 py-6 text-sm">
            <Check className="h-4 w-4 text-emerald-600" />
            <span>
              Sent to <span className="font-medium">{sentTo}</span>.
            </span>
          </div>
        ) : (
          <div className="space-y-3">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search channels or people..."
              aria-label="Search channels or people"
              autoFocus
            />

            <div className="max-h-56 overflow-y-auto rounded-md border" role="listbox">
              {loadingChannels ? (
                <div className="px-3 py-4 text-sm text-muted-foreground">
                  Loading conversations…
                </div>
              ) : channelsQuery.isError ? (
                <div className="px-3 py-4 text-sm text-destructive">
                  Could not load your conversations.
                </div>
              ) : nothingToShow ? (
                <div className="px-3 py-4 text-sm text-muted-foreground">
                  {search.trim() ? 'No matching conversations.' : 'You have no conversations yet.'}
                </div>
              ) : (
                <>
                  {filteredChannels.map(({ channel, label }) => {
                    const selected =
                      target?.kind === 'channel' && target.channelId === channel.channelId;
                    return (
                      <button
                        key={channel.channelId}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() =>
                          setTarget({ kind: 'channel', channelId: channel.channelId, label })
                        }
                        className={cn(
                          'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted',
                          selected && 'bg-muted',
                        )}
                      >
                        {channel.channelType === 'direct' ? (
                          <User className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                        ) : (
                          <Hash className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                        )}
                        <span className="truncate">{label}</span>
                        {selected && <Check className="ml-auto h-3.5 w-3.5 text-primary" />}
                      </button>
                    );
                  })}
                  {filteredPeople.map((person) => {
                    const label = person.displayName || person.username;
                    const selected = target?.kind === 'user' && target.userId === person.userId;
                    return (
                      <button
                        key={person.userId}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => setTarget({ kind: 'user', userId: person.userId, label })}
                        className={cn(
                          'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted',
                          selected && 'bg-muted',
                        )}
                      >
                        <MessageCircle className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                        <span className="truncate">{label}</span>
                        <span className="truncate text-xs text-muted-foreground">
                          @{person.username}
                        </span>
                        {selected && <Check className="ml-auto h-3.5 w-3.5 text-primary" />}
                      </button>
                    );
                  })}
                </>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="send-ticket-note" className="text-xs text-muted-foreground">
                Note (optional)
              </Label>
              <Textarea
                id="send-ticket-note"
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX_LENGTH))}
                placeholder="Add context for whoever picks this up..."
                rows={2}
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {sentTo ? 'Close' : 'Cancel'}
          </Button>
          {!sentTo && (
            <Button onClick={handleSend} disabled={!target || sending || !ticket}>
              {sending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {sending ? 'Sending…' : 'Send'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
